/**
 * consent-actor.ts — formatting helpers for the App Posture "Recent consents"
 * feed (§8.5). The row type lives here until the Container endpoint ships its
 * DTO; that's the only place it's consumed today.
 */

export type ConsentKind =
  | 'UserDelegated'
  | 'AdminDelegated'
  | 'AdminAppRole'
  | 'AllPrincipals'

export interface ConsentRecordRow {
  id: string
  servicePrincipalId: string
  appDisplayName: string
  appIsVerifiedPublisher: boolean
  /** Null for AdminAppRole / AllPrincipals — Graph doesn't surface the granting admin. */
  principalDisplayName: string | null
  principalUpn: string | null
  consentKind: ConsentKind
  scope: string
  highestPermissionTier: 'Critical' | 'High' | 'Medium' | 'Low'
  /** ISO 8601. */
  grantedAt: string
}

/**
 * Format the actor for a feed row. The 'an admin' fallback is honest — for
 * admin-consent flows, the Graph oauth2PermissionGrants payload doesn't
 * include the granting admin's identity; that requires the directoryAudits
 * endpoint, tracked as a Phase D follow-up.
 */
export function consentActor(row: ConsentRecordRow): string {
  switch (row.consentKind) {
    case 'UserDelegated':
      return row.principalDisplayName ?? row.principalUpn ?? 'a user'
    case 'AdminDelegated':
    case 'AdminAppRole':
      return row.principalDisplayName ?? 'an admin'
    case 'AllPrincipals':
      return 'tenant-wide'
  }
}

/**
 * Split an ISO timestamp into "DD MMM" + "HH:MM UTC" parts. The mockup's feed
 * row layout shows the date on a bold line above the time.
 */
export function formatConsentTimestamp(iso: string): { date: string; time: string } {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return { date: '—', time: '' }
  const dateFmt = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    timeZone: 'UTC',
  })
  const timeFmt = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  })
  return {
    date: dateFmt.format(d),
    time: `${timeFmt.format(d)} UTC`,
  }
}
